import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import underline14 from 'stile-au/static/underline-14.png';
import underline16Mobile from 'stile-au/static/underline-16-mobile.svg';
import styled from 'styled-components';

import iconPage from 'stile-shared/assets/icons/icon_page.svg';

import { TeaserBlock } from 'stile-shared/src/components/2020/blocks/TeaserBlock/TeaserBlock';
import { Button } from 'stile-shared/src/components/2024/common/Button';
import { HearFromSection } from 'stile-shared/src/components/2024/common/HearFromSection';
import { Icon } from 'stile-shared/src/components/2024/common/Icon';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import { RealWorldSection } from 'stile-shared/src/components/2024/common/RealWorldSection';
import PageLinkFeature from 'stile-shared/src/components/2024/layout/PageLinkFeature';
import { TopSection } from 'stile-shared/src/components/2024/layout/RegionBasedTopSection';
import ReviewsSlider from 'stile-shared/src/components/2024/layout/ReviewsSlider/ReviewsSlider';
import { SideBySideSection } from 'stile-shared/src/components/2024/layout/SideBySideSection';

import { PageLayout } from 'templates/2024/PageLayout';

export { Head } from 'templates/2024/Head';

const NSWLayout = styled(PageLayout)`
  p {
    margin-bottom: 12px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  .underline-14 {
    display: inline-block;
    position: relative;

    &::after {
      bottom: 5px;
      content: '';
      display: block;
      position: absolute;
      background-repeat: no-repeat;
      background-position: top left;
      background-size: cover;
      left: 5px;
      width: 100%;
      height: 8px;
      background-image: url('${underline14}');
    }
  }
  @media (max-width: 768px) {
    .underline-14:after {
      background-image: url('${underline16Mobile}');
      bottom: -9px;
      left: -5px;
      width: 109px;
      height: 22px;
    }
    .header-wrapper {
      background-color: var(--stile-grey-1);
    }
  }
`;

const NSWTopSection = styled(TopSection)`
  width: 100%;
  p {
    margin-bottom: 12px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  .content {
    padding: 80px 0;
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-gap: 20px;

    .--left {
      width: 100%;
      grid-column: 1 / 7; /* 6 columns */
      display: flex;
      flex-direction: column;
      gap: 0;

      h1 {
        margin: 40px 0px 32px;
        width: 100%; /* full width of the 6 columns */
      }

      .--feature_heading {
        margin: 0;
        width: 83.33%; /* 5/6 of the left container = 5/12 of total */
      }
    }

    .--right {
      width: 100%;
      grid-column: 7 / 13; /* 6 columns */
      display: flex;
      align-items: center;
      justify-content: center;

      .gatsby-image-wrapper {
        margin-top: 0px;
        aspect-ratio: 1126 / 1002;
        width: 100%;
        height: auto;
      }
    }
  }
  @media (max-width: 768px) {
    background-color: var(--stile-grey-1);
    .content {
      padding: 0 25px 60px 25px;
      margin: 0;
      display: block;

      .--left {
        h1 {
          margin: 25px 0px 20px;
        }

        .--feature_heading {
          margin: 0 0 30px;
          width: 100%;
        }
      }

      .--right {
        width: 100%;
        .gatsby-image-wrapper {
          width: 100% !important;
          height: auto;
          margin-bottom: 0px;
          aspect-ratio: 1126 / 1002;

          img {
            object-fit: contain !important;
            width: 100% !important;
            height: 100% !important;
          }
        }
      }
    }
  }
`;

const StyledImageWrapper = styled.div`
  display: flex;
  min-width: 267px;
  justify-content: center;
  .greyBorder {
    border-color: var(--stile-grey-1);
    border-style: solid;
    border-width: 2px;
  }
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.2);
  }

  .gatsby-image-wrapper,
  .overlay {
    border-radius: 32px;
  }
  .cover,
  .withShadow,
  .overlay {
    box-shadow: 0px 41.958px 25.37px 0px rgba(0, 0, 0, 0.04),
      0px 18.54px 18.54px 0px rgba(0, 0, 0, 0.06), 0px 4.879px 10.733px 0px rgba(0, 0, 0, 0.07);
  }
  @media (max-width: 768px) {
    .gatsby-image-wrapper,
    .overlay {
      border-radius: 16px !important;
    }
  }
`;

const CurriculumSection = styled(SideBySideSection)`
  margin-top: 150px;
  .right-text a {
    margin-top: 32px;
  }
  h2 {
    margin-bottom: 32px;
    margin-top: 0;
  }

  @media (max-width: 768px) {
    margin-top: 60px;
    .right-text a {
      margin-top: 28px;
    }
  }
`;

const WebinarSection = styled.div`
  margin-top: 150px;
  margin-bottom: 150px;
  display: flex;
  justify-content: center;
  position: relative;

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 1200px;
    width: 100%;
    gap: 80px;
  }

  h2 {
    text-align: center;
    width: 66.67%; /* 8/12 columns */

    @media (max-width: 768px) {
      width: 100%;
      text-align: left;
    }
  }

  p {
    margin-top: 12px !important;
  }

  .cards-container {
    display: flex;
    gap: 40px;
    width: 100%;
    /* Ensure equal-width columns for children */
    > .teaser-block {
      flex: 1 1 0;
    }

    @media (max-width: 1024px) {
      flex-direction: column;
      gap: 40px;
    }
  }

  @media (max-width: 768px) {
    margin-top: 60px;
    width: 100%;
    .content {
      flex-direction: column;
      align-items: flex-start;
      gap: 40px;
      width: 100%;
      padding: 0 25px 0 25px;
      margin: 0;
    }
  }

  .teaser-block {
    margin: 0;
    padding: 0;

    a {
      margin-top: 32px !important;
    }
  }

  .card-image {
    width: 100%;
    overflow: visible;
    position: relative;

    .gatsby-image-wrapper {
      width: 100% !important;
      height: 100% !important;

      img {
        object-fit: cover !important;
        width: 100% !important;
        height: 100% !important;
        border-radius: inherit;
      }

      picture {
        width: 100% !important;
        height: 100% !important;
        border-radius: inherit;
      }

      picture img {
        object-fit: cover !important;
        width: 100% !important;
        height: 100% !important;
        border-radius: inherit;
      }
    }
  }
`;

function NSWSyllabusPage(props: PageProps) {
  return (
    <NSWLayout {...props}>
      <NSWTopSection>
        <div className="content">
          <div className="--left">
            <h1>
              The complete <span className="underline-14">NSW</span> Science program
            </h1>
            <p className="--feature_heading">
              Aligned to the new NSW Science syllabus, Stile is the only complete program with a
              ready-to-teach scope and sequence.
            </p>
          </div>
          <div className="--right">
            <StaticImage
              src="../../../../stile-shared/assets/images/nsw/nsw_map.png"
              alt="Map of New South Wales with many green glowing dots showing the locations of Stile schools, with small illustrations of students and a scientist around the edges."
              loading="eager"
            />
          </div>
        </div>
      </NSWTopSection>

      <ReviewsSlider />

      <CurriculumSection>
        <div className="image-left content">
          <div className="left-img">
            <StyledImageWrapper>
              <StaticImage
                className="cover"
                src="../../../../stile-shared/assets/images/nsw/nsw_syllabus_imp_guide.png"
                alt="Cover page of a document titled “The New South Wales Science 7–10 Syllabus: Our guide to preparing for implementation.” Stile logo in green on a light background."
                placeholder="none"
                loading="lazy"
              />
            </StyledImageWrapper>
          </div>
          <div className="right-text">
            <h2>Built for the NSW syllabus. Ready for every classroom.</h2>
            <p>
              The <b>new science syllabus</b> brings more structure, new content, and stronger
              expectations. Stile is already aligned and ready to teach. Our updated Years 7–10
              units cover each focus area in Stages 4 and 5, with lessons mapped directly to NESA
              outcomes. The result is a seamless, structured program that saves teachers time while
              supporting consistency across classrooms.
            </p>
            <p>
              <b>Save hours of planning</b>
              <br />
              Get the Implementation Guide with a full scope and sequence for Stages 4 & 5:
            </p>
            <Button
              className="iconButton"
              newTab={true}
              href="/resources/new-nsw-syllabus/"
              label={
                <>
                  <Icon src={iconPage} iconSize="20px" style={{ marginRight: 6 }} />
                  <span>Download implementation guide</span>
                </>
              }
            />
          </div>
        </div>
      </CurriculumSection>

      <WebinarSection>
        <div className="content">
          <h2>What's changed, and how to teach the new syllabus</h2>
          <div className="cards-container">
            <TeaserBlock
              className="teaser-block"
              contentSide="bottom"
              asideMax="none"
              image={
                <div className="card-image">
                  <Link
                    openInNewTab
                    url="https://www.bigmarker.com/stile-education1/The-new-science-syllabus-What-s-changed?utm_bmcr_source=Landingpage"
                  >
                    <StyledImageWrapper>
                      <StaticImage
                        className="withShadow"
                        src="../../../../stile-shared/assets/images/nsw/nsw_syllabus_video_1.jpg"
                        alt="Pink slide with a black-and-white headshot of a woman and the text “The new science syllabus: What’s changed? On-Demand PL."
                      />
                    </StyledImageWrapper>
                  </Link>
                </div>
              }
            >
              <h4>The new science syllabus: What's changed?</h4>
              <p>
                <strong>30 mins | Presented by Alex Russell, Curriculum Specialist at Stile</strong>
              </p>
              <p>
                A roundtable on classroom realities, common roadblocks, and how to get your team
                syllabus-ready.
              </p>
              <Button
                href="https://www.bigmarker.com/stile-education1/The-new-science-syllabus-What-s-changed?utm_bmcr_source=Landingpage"
                newTab={true}
                label="Watch recording"
              />
            </TeaserBlock>
            <TeaserBlock
              className="teaser-block"
              contentSide="bottom"
              asideMax="none"
              image={
                <div className="card-image">
                  <Link
                    openInNewTab
                    url="https://www.bigmarker.com/stile-education1/The-new-NSW-Syllabus-85336525faf27ba6b8151454-569abb61399f2d99ede52728?utm_bmcr_source=marketingsite"
                  >
                    <StyledImageWrapper>
                      <StaticImage
                        className="withShadow"
                        src="../../../../stile-shared/assets/images/nsw/nsw_syllabus_video_2.jpg"
                        alt="Blue slide with three circular headshots of presenters and the text “Implementing the new science syllabus with Stile. On-Demand PL.”"
                      />
                    </StyledImageWrapper>
                  </Link>
                </div>
              }
            >
              <h4>Implementing the New Syllabus in Real Schools</h4>
              <p>
                <strong>
                  30 mins | With Bill Matchett (Macquarie Fields HS) and Dr Simon Crook (CrookED
                  Science)
                </strong>
              </p>
              <p>
                Understand the five key shifts and what they mean for programming, outcomes, and
                classroom delivery.
              </p>
              <Button
                href="https://www.bigmarker.com/stile-education1/The-new-NSW-Syllabus-85336525faf27ba6b8151454-569abb61399f2d99ede52728?utm_bmcr_source=marketingsite"
                newTab={true}
                label="Watch recording"
              />
            </TeaserBlock>
          </div>
        </div>
      </WebinarSection>

      <HearFromSection
        heading="Hear from science teacher Chris and principal David"
        subheading={
          <>
            <p>
              Stile’s scope and sequence is a world-class starting point that’s fully editable and
              customisable to your school’s outcomes, priorities and teaching styles.
            </p>
            <p>
              From ready-to-adapt program design to student progress feedback, Stile supports the
              complete teaching workflow with customisable lesson programs, always up-to-date
              content, high-quality instructional materials and strategies to balance device use and
              increase student learning and engagement.
            </p>
          </>
        }
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/nsw/nsw_testimonial_video_cover.jpg"
            alt="thumbnail from Stile YouTube testimonial video"
          />
        }
        youtubeUrl="https://www.youtube.com/watch?v=4lFzzbHRNpY"
      />

      <RealWorldSection
        heading="Here's what's new in the science syllabus"
        tabs={[
          'Introduction of focus areas',
          'An emphasis on data science',
          'Introduction of Depth Studies',
          'First Nations knowledges',
        ]}
        headings={[
          'Bringing science together',
          'From data to decision-making',
          'Developing scientific research skills',
          'Incorporating First Nations histories and cultures',
        ]}
        multiParagraphs={[
          [
            'The 2023 NSW Science syllabus spans 16 focus areas across Stages 4 and 5, with each one exploring science understanding, working scientifically and science as a human endeavour.',
            'In Stile, each focus area is explored through engaging units that connect science to real life. For example, Introduction to Science and Our Place in Space guide students through Observing the Universe, while Forces and Magnetism let them investigate the pushes and pulls shaping our world.',
            'This approach gives teachers a clear progression across Years 7–10 while keeping science meaningful for students.',
          ],
          [
            'Data Science is a dedicated focus area in the new science syllabus. Students collect, process and analyse real-world datasets to identify patterns and make evidence-based decisions, reflecting the growing importance of data literacy in science and industry.',
            'Data Science 1 is paired with Change in Stage 4, where students explore models, and Data Science 2 with Genetics and evolutionary change in Stage 5, where they examine pseudoscience. Outcomes are also scaffolded across Years 7–10, ensuring students build authentic data science skills in engaging, meaningful contexts.',
          ],
          [
            'In the new syllabus, students in both Stages 4 and 5 must complete a yearly Depth Study to foster critical thinking and deeper connections to science.',
            'Stile’s NSW scope and sequence includes one Depth Study each year, supported with purpose-built units that scaffold the process. In some years, students choose their own focus area to explore; in others, the depth study is built into a unit. In both cases, students are guided to design questions, collect and analyse data, and present findings, building a portfolio of scientific skills.',
          ],
          [
            'The NSW syllabus incorporates explicit links to Aboriginal and Torres Strait Islander Peoples’ Cultural and Scientific Ways of Knowing, Being and Doing. Ten of the sixteen focus areas now include content descriptors that address this directly, reflecting both the syllabus and the NSW Department of Education’s Partnership Agreement with the Aboriginal Education Consultative Group (AECG).',
            'Units and lessons have been developed through respectful collaboration with First Nations educators, scientists and illustrators. Students learn from examples such as spear throwers in our updated Forces unit, Wilgie Mia in the Periodic Table, and Aboriginal astronomy in Our Place in Space. All resources are reviewed to ensure cultural safety and the protection of Indigenous Cultural and Intellectual Property, giving teachers confidence that this content is embedded safely and appropriately.',
          ],
        ]}
        images={[
          <StyledImageWrapper key={0}>
            <StaticImage
              className="greyBorder"
              src="../../../../stile-shared/assets/images/nsw/nsw_whats_new_tab_1.png"
              alt="Curriculum focus areas displayed with images and text: Term 1 “Genetics and evolutionary change” with topics on cancer and superbugs, and Term 2 “Energy” with topics on hydropower and sustainable transport."
            />
          </StyledImageWrapper>,
          <StyledImageWrapper key={1}>
            <StaticImage
              src="../../../../stile-shared/assets/images/nsw/nsw_whats_new_tab_2.jpg"
              alt="Three students in school uniforms wearing safety goggles and blue aprons working on a science experiment at a lab bench."
            />
          </StyledImageWrapper>,
          <StyledImageWrapper key={2}>
            <StaticImage
              src="../../../../stile-shared/assets/images/nsw/nsw_whats_new_tab_3.jpg"
              alt="Two high school students looking closely at plants in a plastic tub, with more greenery in the foreground."
            />
          </StyledImageWrapper>,
          <StyledImageWrapper key={3}>
            <StaticImage
              src="../../../../stile-shared/assets/images/nsw/nsw_whats_new_tab_4.jpg"
              alt="Close-up of several Aboriginal tools with black resin-coated tips and wooden handles, some painted or decorated, laid out in a row."
            />
          </StyledImageWrapper>,
        ]}
      />

      <PageLinkFeature
        heading="Be supported by our NSW team of experts"
        copy={
          <div>
            <p>
              Stile is all about empowering teachers with the best support. In New South Wales, that
              means teaming up with seasoned educators who have extensive experience in teaching,
              administration and instructional coaching.
            </p>
            <p>Together, teachers are supported and science lessons are brought to life.</p>
          </div>
        }
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/nsw/nsw_team_portraits.png"
            alt="Three Stile team members against colorful science-themed backgrounds."
            quality={100}
          />
        }
        button={
          <Button
            className="setUpTrialButton"
            href="/set-up-trial/"
            label={
              <>
                <span>Learn about trialling Stile at your school</span>
              </>
            }
          />
        }
      />
    </NSWLayout>
  );
}

export default NSWSyllabusPage;
